import React, { useState, useRef, useEffect } from "react";
import SendIcon from "@mui/icons-material/Send";
import tinycolor from "tinycolor2";
import RefreshIcon from "@mui/icons-material/Refresh";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import EventList from "./EventList";
import ProductList from "./ProductList";
import ArticleList from "./ArticleList";
import PropTypes from "prop-types";
import LinkIcon from "@mui/icons-material/Link";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionIcon from "@mui/icons-material/Description";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const widgetStyles = {
  position: "fixed",
  bottom: "20px",
  right: "20px",
  zIndex: 9999,
  fontFamily: "'Inter', sans-serif",
};

const pillButtonStyle = (bgColor) => ({
  display: "flex",
  alignItems: "center",
  background: bgColor,
  color: tinycolor.mostReadable(bgColor, ["#fff", "#222"]).toHexString(),
  padding: "10px 18px 10px 10px",
  borderRadius: "30px",
  cursor: "pointer",
  border: `1px solid ${tinycolor(bgColor).isLight() ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.2)"}`,
  transition: "transform 0.18s ease-out, box-shadow 0.18s ease-out",
  animation: "pulseGlow 2.5s infinite ease-in-out",
});

const pillButtonHoverStyles = {
  transform: "scale(1.03)",
  boxShadow: "0 6px 20px rgba(0,0,0,0.2)",
};

const pillAvatarContainerStyle = {
  width: "44px",
  height: "44px",
  borderRadius: "50%",
  marginRight: "10px",
  background: "rgba(255,255,255,0.2)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  overflow: "hidden",
  flexShrink: 0,
};

const pillAvatarImageStyle = {
  width: "100%",
  height: "100%",
  objectFit: "cover",
};

const pillTextStyle = {
  fontSize: "17px",
  fontWeight: "600",
};

const chatBoxStyles = {
  position: "absolute",
  bottom: "0",
  right: 0,
  width: "400px",
  background: "#fff",
  borderRadius: "16px",
  boxShadow: "0 6px 32px rgba(0,0,0,0.22)",
  padding: "0",
  overflow: "hidden",
  border: "1px solid #e0e0e0",
  display: "flex",
  flexDirection: "column",
  height: "600px",
  transition: "width 0.3s ease, height 0.3s ease, box-shadow 0.3s ease",
};

const expandedChatBoxStyles = {
  width: "90vw",
  height: "90vh",
  maxWidth: "1200px",
  maxHeight: "900px",
};

const headerStyles = {
  background: "#1976d2",
  color: "#fff",
  padding: "18px 16px 18px 72px",
  fontWeight: "bold",
  fontSize: "18px",
  position: "relative",
  display: "flex",
  alignItems: "center",
  minHeight: "56px",
};

const logoCircleStyles = (color) => ({
  position: "absolute",
  left: "16px",
  top: "50%",
  transform: "translateY(-50%)",
  width: "40px",
  height: "40px",
  borderRadius: "50%",
  background: color,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: "0 2px 8px rgba(0,0,0,0.10)",
  color: "#fff",
  fontWeight: "bold",
  fontSize: "20px",
  textTransform: "uppercase",
});

const avatarImageStyles = {
  position: "absolute",
  left: "16px",
  top: "50%",
  transform: "translateY(-50%)",
  width: "40px",
  height: "40px",
  borderRadius: "50%",
  objectFit: "cover",
  boxShadow: "0 2px 8px rgba(0,0,0,0.10)",
};

const logoImgStyles = {
  width: "28px",
  height: "28px",
  objectFit: "contain",
  borderRadius: "50%",
  background: "#fff",
};

const bodyStyles = {
  padding: "16px",
  minHeight: "120px",
  fontSize: "15px",
  color: "#333",
  flex: 1,
  overflowY: "auto",
  background: "#f8f9fa",
  display: "flex",
  flexDirection: "column",
  gap: "0px",
  transition: "font-size 0.3s ease, padding 0.3s ease",
};

const expandedBodyStyles = {
  fontSize: "18px",
  padding: "24px 32px",
};

const closeBtnStyles = {
  position: "absolute",
  top: "8px",
  right: "12px",
  background: "none",
  border: "none",
  color: "#fff",
  fontSize: "28px",
  cursor: "pointer",
  lineHeight: 1,
  width: "36px",
  height: "36px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "background 0.15s",
};

const inputBoxStyles = {
  display: "flex",
  padding: "12px 16px",
  borderTop: "none",
  background: "transparent",
  gap: "10px",
  alignItems: "center",
};

const inputStyles = {
  flex: 1,
  padding: "12px 18px",
  borderRadius: "24px",
  border: "none",
  backgroundColor: "#f1f3f6",
  fontSize: "15px",
  color: "#333",
  outline: "none",
  transition: "font-size 0.3s ease, padding 0.3s ease",
};

const expandedInputStyles = {
  fontSize: "18px",
  padding: "16px 24px",
};

const sendBtnStyles = {
  width: "48px",
  height: "48px",
  borderRadius: "50%",
  border: "none",
  background: "#1976d2",
  color: "#fff",
  cursor: "pointer",
  fontWeight: "bold",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "background 0.2s",
};

const userMsgStyles = {
  alignSelf: "flex-end",
  background: "#1976d2",
  color: "#fff",
  borderRadius: "18px 18px 6px 18px",
  padding: "12px 18px",
  margin: "6px 0",
  maxWidth: "75%",
  fontSize: "15px",
  border: "none",
  boxShadow: "0 2px 8px rgba(25, 118, 210, 0.10)",
  wordBreak: "break-word",
  textAlign: "right",
};

const expandedUserMsgStyles = {
  fontSize: "18px",
  padding: "16px 24px",
};

const assistantMsgStyles = {
  alignSelf: "flex-start",
  background: "#f1f3f6",
  color: "#222",
  borderRadius: "18px 18px 18px 6px",
  padding: "12px 18px",
  margin: "6px 0",
  maxWidth: "75%",
  fontSize: "15px",
  border: "none",
  boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
  wordBreak: "break-word",
  textAlign: "left",
};

const expandedAssistantMsgStyles = {
  fontSize: "18px",
  padding: "16px 24px",
};

// Typing dots animácia
const typingWrapperStyles = {
  display: "flex",
  alignItems: "center",
  height: "32px",
  margin: "6px 0",
};
const typingDotStyles = {
  width: "8px",
  height: "8px",
  borderRadius: "50%",
  background: "#b0b4bb",
  margin: "0 3px",
  animation: "typing-bounce 1.2s infinite both",
  display: "inline-block",
};
const typingDot2 = { ...typingDotStyles, animationDelay: "0.2s" };
const typingDot3 = { ...typingDotStyles, animationDelay: "0.4s" };

// Animácia cez style tag
const typingKeyframes = `
@keyframes typing-bounce {
  0%, 80%, 100% { transform: scale(0.7); opacity: 0.7; }
  40% { transform: scale(1); opacity: 1; }
}`;

const pulseGlowKeyframes = (color) => `
@keyframes pulseGlow {
  0% {
    box-shadow: 0 4px 14px rgba(0,0,0,0.15), 0 0 0 0px ${tinycolor(color)
      .setAlpha(0.3)
      .toRgbString()};
  }
  50% {
    box-shadow: 0 6px 18px rgba(0,0,0,0.20), 0 0 0 10px ${tinycolor(color)
      .setAlpha(0)
      .toRgbString()};
  }
  100% {
    box-shadow: 0 4px 14px rgba(0,0,0,0.15), 0 0 0 0px ${tinycolor(color)
      .setAlpha(0)
      .toRgbString()};
  }
}
`;

const headerButton = {
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  background: "transparent",
  border: "none",
  color: "#fff",
  cursor: "pointer",
  borderRadius: "50%",
  width: "38px",
  height: "38px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "background 0.15s",
};

const headerButtonHoverBg = "rgba(255, 255, 255, 0.12)";

// Štýly pre Markdown obsah vnútri bubliny
const markdownStyles = `
  .assistant-message-content p {
    margin: 0;
  }
  .assistant-message-content ul, .assistant-message-content ol {
    margin: 8px 0 8px 20px;
    padding: 0;
  }
  .assistant-message-content li {
    margin-bottom: 4px;
  }
  .assistant-message-content strong {
    font-weight: 600;
  }
`;

export default function Widget({ config: configProp, token: tokenProp, adminMode = false }) {
  // Rozšírený default config s podporou viacerých kľúčov
  const defaultConfig = {
    name: "Chatbot",
    headerColor: "#1976d2",
    buttonColor: "#1976d2",
    textColor: "#222",
    backgroundColor: "#f8f9fa",
    welcomeMessage: "",
    buttonText: "Ako Vám môžem pomôcť?",
  };

  const getInitialHistory = () => {
    if (adminMode) return [];
    try {
      const stored = localStorage.getItem("chatbot_history");
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  };
  const getInitialOpen = () => {
    if (adminMode) return true;
    try {
      const stored = localStorage.getItem("chatbot_open");
      return stored ? JSON.parse(stored) : false;
    } catch {
      return false;
    }
  };
  const getInitialConfig = () => {
    // Pre uncontrolled mode
    if (adminMode) return defaultConfig; // V admin mode sa config riadi props
    try {
      const stored = localStorage.getItem("chatbot_config");
      return stored ? { ...defaultConfig, ...JSON.parse(stored) } : defaultConfig;
    } catch {
      return defaultConfig;
    }
  };

  const [open, setOpen] = useState(getInitialOpen());
  const [message, setMessage] = useState("");
  const [history, setHistory] = useState(getInitialHistory());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [internalConfig, setInternalConfig] = useState(getInitialConfig);
  const messagesEndRef = useRef(null);
  const [pillHover, setPillHover] = useState(false);
  const [suggestedQuestionsVisible, setSuggestedQuestionsVisible] = useState(true);
  const [expanded, setExpanded] = useState(false);
  // Pridávam detekciu mobilu
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 600 : false
  );

  // Finálny config objekt - priorita je prop, inak interný stav
  const config = adminMode && configProp ? { ...defaultConfig, ...configProp } : internalConfig;

  // Ukladanie histórie do localStorage pri zmene (len ak nie je adminMode)
  useEffect(() => {
    if (adminMode) return;
    try {
      localStorage.setItem("chatbot_history", JSON.stringify(history));
    } catch {}
  }, [history, adminMode]);

  // Ukladanie stavu otvorenia do localStorage pri zmene (len ak nie je adminMode)
  useEffect(() => {
    if (adminMode) return;
    try {
      localStorage.setItem("chatbot_open", JSON.stringify(open));
    } catch {}
  }, [open, adminMode]);

  // Ukladanie configu do localStorage pri zmene (len ak nie je configProp a nie je adminMode)
  useEffect(() => {
    if (adminMode || configProp) return;
    try {
      localStorage.setItem("chatbot_config", JSON.stringify(internalConfig));
    } catch {}
  }, [internalConfig, configProp, adminMode]);

  // Získanie hodnoty chatbot-id zo script tagu (len ak nie je tokenProp)
  let chatbotToken = tokenProp;
  if (!chatbotToken && typeof window !== "undefined") {
    const script = document.querySelector('script[src="https://rieshim.app/widget.js"]');
    chatbotToken = script ? script.getAttribute("chatbot-id") : null;
  }

  // Načítanie configu podľa tokenu (len ak nie je configProp a nie je adminMode)
  useEffect(() => {
    if (adminMode || configProp) return; // V admin mode sa nenačítava
    if (!chatbotToken) return;
    fetch(`https://rieshim.app/api/chatbots/config/${chatbotToken}`)
      .then((res) => res.json())
      .then((data) => {
        if (data && data.config) {
          const merged = { ...defaultConfig, ...data.config };
          setInternalConfig(merged);
          try {
            localStorage.setItem("chatbot_config", JSON.stringify(merged));
          } catch {}
        }
      })
      .catch(() => {
        setInternalConfig(defaultConfig);
      });
  }, [chatbotToken, configProp, adminMode]);

  // Pridávam detekciu mobilu
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 600);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleSendMessage = async (text) => {
    if (!text || !text.trim()) return;
    if (!chatbotToken) {
      setError("Chýba token pre chatbota");
      return;
    }
    setLoading(true);
    setError("");
    setSuggestedQuestionsVisible(false); // Skryjeme po odoslaní

    const newHistory = [...history, { role: "user", content: text }];
    setHistory(newHistory);

    try {
      // Odstráň custom polia pred odoslaním do backendu
      const groqHistory = newHistory.map((msg) => ({ role: msg.role, content: msg.content }));
      const completionsBody = {
        messages: groqHistory,
        max_completion_tokens: 150,
        current_url: window.location.href,
        current_time: new Date().toISOString(),
      };
      const res = await fetch("https://rieshim.app/v1/chat/completions", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: "Bearer " + chatbotToken },
        body: JSON.stringify(completionsBody),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.detail || "Chyba pri komunikácii so serverom");
        setLoading(false);
        return;
      }
      setHistory([
        ...newHistory,
        {
          role: "assistant",
          content: data.choices?.[0]?.message?.content || "(žiadna odpoveď)",
          source_url: data.source_url || null,
          attachments: data.attachments || null,
          waste_dates: data.waste_dates || null,
          products: data.products || null,
          articles: data.articles || null,
          follow_up_questions: data.follow_up_questions || null,
        },
      ]);
    } catch (err) {
      setError("Chyba siete alebo servera");
    }
    setLoading(false);
  };

  const handleSend = async (e) => {
    if (e) e.preventDefault();
    if (!message.trim()) return;
    handleSendMessage(message);
    setMessage("");
  };

  const handleSuggestedQuestionClick = (question) => {
    handleSendMessage(question);
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [history, open]);

  const headerBg = config?.headerColor || defaultConfig.headerColor;
  const buttonBase = config?.buttonColor || headerBg;
  const bodyBg = config?.backgroundColor || defaultConfig.backgroundColor;

  const suggestedQuestionStyle = {
    background: "none",
    border: `1px solid ${
      tinycolor(buttonBase).isValid() ? tinycolor(buttonBase).toRgbString() : "#1976d2"
    }`,
    color: `${tinycolor(buttonBase).isValid() ? tinycolor(buttonBase).toRgbString() : "#1976d2"}`,
    padding: "8px 12px",
    borderRadius: "16px",
    cursor: "pointer",
    margin: "4px",
    fontSize: "14px",
    transition: "background 0.2s",
    textAlign: "left",
  };

  const baseForUserMsg = tinycolor(buttonBase);
  const userMsgBg = baseForUserMsg.isLight()
    ? baseForUserMsg.darken(10).toHexString()
    : baseForUserMsg.lighten(20).toHexString();

  const userMsgColor = tinycolor.mostReadable(userMsgBg, ["#222", "#fff"]).toHexString();
  const sendBtnBg = tinycolor(buttonBase).darken(8).toHexString();
  const sendBtnBgHover = tinycolor(buttonBase).darken(16).toHexString();

  const followUpTitleStyle = {
    fontSize: "14px",
    fontWeight: "600",
    color: config?.textColor || defaultConfig.textColor,
    opacity: 0.85,
    paddingLeft: "4px",
  };

  const userMsgStyles = {
    alignSelf: "flex-end",
    background: userMsgBg,
    color: userMsgColor,
    borderRadius: "18px 18px 6px 18px",
    padding: "12px 18px",
    margin: "6px 0",
    maxWidth: "75%",
    fontSize: "15px",
    border: "none",
    boxShadow: `0 2px 8px ${tinycolor(headerBg).setAlpha(0.1).toRgbString()}`,
    wordBreak: "break-word",
    textAlign: "right",
    transition: "font-size 0.3s ease, padding 0.3s ease",
  };

  const expandedUserMsgStyles = {
    fontSize: "18px",
    padding: "16px 24px",
  };

  const assistantMsgStyles = {
    alignSelf: "flex-start",
    background: "#f1f3f6",
    color: config?.textColor || defaultConfig.textColor,
    borderRadius: "18px 18px 18px 6px",
    padding: "12px 18px",
    margin: "6px 0",
    maxWidth: "75%",
    fontSize: "15px",
    border: "none",
    boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
    wordBreak: "break-word",
    textAlign: "left",
  };

  const sendBtnStyles = {
    width: "48px",
    height: "48px",
    borderRadius: "50%",
    border: "none",
    background: sendBtnBg,
    color: "#fff",
    cursor: "pointer",
    fontWeight: "bold",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "background 0.2s",
  };

  // Funkcia na resetovanie chatu a localStorage (v adminMode len resetuje state)
  const handleResetChat = () => {
    if (!adminMode) {
      try {
        localStorage.removeItem("chatbot_history");
        localStorage.removeItem("chatbot_open");
      } catch {}
    }
    setHistory([]);
    setOpen(true);
    setError("");
    setSuggestedQuestionsVisible(true);
  };

  // Mobilné štýly
  const mobileChatBoxStyles = {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    background: "#fff",
    borderRadius: 0,
    boxShadow: "0 0 0 rgba(0,0,0,0)",
    padding: 0,
    overflow: "hidden",
    border: "none",
    display: "flex",
    flexDirection: "column",
    zIndex: 99999,
  };
  const mobileHeaderStyles = {
    ...headerStyles,
    borderRadius: 0,
    minHeight: 56,
    fontSize: 17,
    padding: "16px 12px 16px 56px",
    background: headerBg,
    color: "#fff",
  };
  const mobileBodyStyles = {
    ...bodyStyles,
    padding: "12px 8px",
    fontSize: 15,
    flex: 1,
    minHeight: 0,
    gap: 0,
  };
  const mobileInputBoxStyles = {
    ...inputBoxStyles,
    padding: "10px 8px",
    gap: 8,
    background: "transparent",
    borderTop: `none`,
  };
  const mobileInputStyles = {
    ...inputStyles,
    fontSize: 16,
    padding: "12px 16px",
  };
  const mobileSendBtnStyles = {
    ...sendBtnStyles,
    width: 44,
    height: 44,
    background: sendBtnBg,
    color: "#fff",
    transition: "background 0.15s",
  };
  const mobileCloseBtnStyles = {
    ...closeBtnStyles,
    top: 10,
    right: 10,
    fontSize: 28,
    width: 36,
    height: 36,
    background: "none",
    color: "#fff",
  };

  // Štýly podľa adminMode
  const widgetStylesFinal = adminMode
    ? { position: "static", width: 500, maxWidth: "100%", fontFamily: "'Inter', sans-serif" }
    : widgetStyles;
  const chatBoxStylesFinal = adminMode
    ? {
        ...chatBoxStyles,
        position: "static",
        bottom: undefined,
        right: undefined,
        height: 600,
        boxShadow: "0 2px 12px rgba(0,0,0,0.10)",
      }
    : chatBoxStyles;

  Widget.propTypes = {
    config: PropTypes.object,
    token: PropTypes.string,
    adminMode: PropTypes.bool,
  };

  return (
    <div style={widgetStylesFinal}>
      {/* Animácia keyframes a import fontu */}
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
          ${typingKeyframes}
          ${pulseGlowKeyframes(headerBg)}
        `}
      </style>
      {(adminMode || open) && (
        <div
          style={
            isMobile && !adminMode
              ? { ...mobileChatBoxStyles, background: bodyBg }
              : {
                  ...chatBoxStylesFinal,
                  ...(expanded && !adminMode ? expandedChatBoxStyles : {}),
                  background: bodyBg,
                }
          }
        >
          {/* Pridané štýly pre Markdown */}
          <style>{markdownStyles}</style>

          <div
            style={
              isMobile && !adminMode
                ? mobileHeaderStyles
                : { ...headerStyles, background: headerBg }
            }
          >
            {config.avatar_url ? (
              <img src={config.avatar_url} alt={config.name} style={avatarImageStyles} />
            ) : (
              <span style={logoCircleStyles(headerBg)}>{config?.name?.[0] || "C"}</span>
            )}
            {config?.name}
            {/* Close tlačidlo a bublina nerenederujem v adminMode */}
            {!adminMode && (
              <button
                style={{ ...headerButton, right: isMobile ? 8 : 10 }}
                onClick={() => setOpen(false)}
                onMouseOver={(e) => (e.currentTarget.style.background = headerButtonHoverBg)}
                onMouseOut={(e) => (e.currentTarget.style.background = "transparent")}
                aria-label="Zavrieť"
              >
                <CloseIcon style={{ fontSize: 24 }} />
              </button>
            )}
            {/* Tlačidlo Obnoviť chat */}
            {!adminMode && (
              <button
                style={{
                  ...headerButton,
                  right: isMobile ? 50 : 54,
                }}
                onClick={handleResetChat}
                onMouseOver={(e) => (e.currentTarget.style.background = headerButtonHoverBg)}
                onMouseOut={(e) => (e.currentTarget.style.background = "transparent")}
                aria-label="Obnoviť chat"
                title="Obnoviť chat"
              >
                <RefreshIcon style={{ fontSize: 22 }} />
              </button>
            )}
            {/* Tlačidlo Zväčšiť/Zmenšiť */}
            {!adminMode && (
              <button
                style={{
                  ...headerButton,
                  right: isMobile ? 92 : 98,
                }}
                onClick={() => setExpanded((v) => !v)}
                onMouseOver={(e) => (e.currentTarget.style.background = headerButtonHoverBg)}
                onMouseOut={(e) => (e.currentTarget.style.background = "transparent")}
                aria-label={expanded ? "Zmenšiť" : "Zväčšiť"}
                title={expanded ? "Zmenšiť" : "Zväčšiť"}
              >
                {expanded ? (
                  <FullscreenExitIcon style={{ fontSize: 26 }} />
                ) : (
                  <FullscreenIcon style={{ fontSize: 26 }} />
                )}
              </button>
            )}
          </div>
          <div
            style={
              isMobile && !adminMode
                ? { ...mobileBodyStyles, background: bodyBg }
                : {
                    ...bodyStyles,
                    ...(expanded && !adminMode ? expandedBodyStyles : {}),
                    background: bodyBg,
                  }
            }
          >
            {history.length === 0 ? (
              config?.welcomeMessage?.trim() ? (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    width: "100%",
                  }}
                >
                  <div style={assistantMsgStyles}>
                    {config.welcomeMessage.split(/\\r?\\n/).map((l, i) => (
                      <div key={i}>{l}</div>
                    ))}
                  </div>
                  {suggestedQuestionsVisible &&
                    config.suggestedQuestions &&
                    config.suggestedQuestions.length > 0 && (
                      <div
                        style={{
                          marginTop: 10,
                          display: "flex",
                          flexWrap: "wrap",
                          justifyContent: "flex-start",
                          paddingLeft: 4,
                        }}
                      >
                        {config.suggestedQuestions.map((q, i) => (
                          <button
                            key={i}
                            style={suggestedQuestionStyle}
                            onClick={() => handleSuggestedQuestionClick(q)}
                            onMouseOver={(e) =>
                              (e.currentTarget.style.background = tinycolor(buttonBase)
                                .setAlpha(0.1)
                                .toRgbString())
                            }
                            onMouseOut={(e) => (e.currentTarget.style.background = "none")}
                          >
                            {q}
                          </button>
                        ))}
                      </div>
                    )}
                </div>
              ) : (
                <div style={{ color: "#888", textAlign: "center", marginTop: 12, fontSize: 14 }}>
                  Začni konverzáciu zadaním správy nižšie.
                </div>
              )
            ) : null}

            {history.map((msg, idx) => {
              if (msg.role === "assistant") {
                // Skús zobraziť source_url ak je k dispozícii
                let sourceUrl = null;
                if (msg.source_url) {
                  sourceUrl = msg.source_url;
                } else {
                  // fallback: hľadaj Viac info: ... v texte
                  const lines = (msg.content || "").split(/\r?\n/);
                  for (const line of lines) {
                    const match = line.match(/^Viac info: (https?:\/\/\S+)/);
                    if (match) {
                      sourceUrl = match[1];
                      break;
                    }
                  }
                }
                // Hlavný text bez Viac info
                const mainText = (msg.content || "")
                  .split(/\r?\n/)
                  .filter((l) => !/^Viac info: (https?:\/\/\S+)/.test(l))
                  .join("\n");
                // Zobrazenie waste_dates ak sú k dispozícii
                let eventList = null;
                if (
                  msg.waste_dates &&
                  Array.isArray(msg.waste_dates) &&
                  msg.waste_dates.length > 0
                ) {
                  // Priradíme farby podľa typu odpadu (alebo default)
                  const colorMap = {
                    papier: "#1976d2",
                    plasty: "#fbc02d",
                    komunál: "#757575",
                    sklo: "#43a047",
                    bio: "#8bc34a",
                    kov: "#bdbdbd",
                    elektro: "#e53935",
                    olej: "#ff9800",
                    šatstvo: "#7b1fa2",
                    nábytok: "#6d4c41",
                    kartóny: "#ffb300",
                    "zberný dvor": "#0288d1",
                  };
                  const items = msg.waste_dates.map((w) => ({
                    date: w.date,
                    title: w.waste_type
                      ? w.waste_type.charAt(0).toUpperCase() + w.waste_type.slice(1)
                      : "",
                    color: colorMap[w.waste_type?.toLowerCase()] || "#1976d2",
                  }));
                  eventList = <EventList items={items} title="Dátumy vývozu odpadu" />;
                }
                return (
                  <div
                    key={idx}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      width: "100%",
                    }}
                  >
                    <div
                      style={{
                        ...assistantMsgStyles,
                        ...(expanded && !adminMode ? expandedAssistantMsgStyles : {}),
                      }}
                      className="assistant-message-content"
                    >
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{mainText}</ReactMarkdown>
                      {sourceUrl && (
                        <div style={{ marginTop: 12 }}>
                          <a
                            href={sourceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "8px",
                              background: "rgba(0,0,0,0.05)",
                              padding: "8px 12px",
                              borderRadius: "12px",
                              textDecoration: "none",
                              color: config?.textColor || defaultConfig.textColor,
                              border: "1px solid rgba(0,0,0,0.08)",
                              fontSize: "14px",
                            }}
                          >
                            <LinkIcon style={{ fontSize: 18, color: "#555" }} />
                            <div>
                              <span style={{ fontWeight: 500 }}>Viac info</span>
                              <span style={{ fontSize: 12, opacity: 0.7, display: "block" }}>
                                {new URL(sourceUrl).hostname}
                              </span>
                            </div>
                          </a>
                        </div>
                      )}
                      {/* Zobrazenie príloh */}
                      {msg.attachments &&
                        Array.isArray(msg.attachments) &&
                        msg.attachments.length > 0 && (
                          <div style={{ marginTop: 12 }}>
                            <div style={{ ...followUpTitleStyle, marginBottom: "8px" }}>
                              Dokumenty na stiahnutie:
                            </div>
                            {msg.attachments.map((att, i) => (
                              <a
                                key={i}
                                href={att.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                  background: "rgba(0,0,0,0.05)",
                                  padding: "8px 12px",
                                  borderRadius: "12px",
                                  textDecoration: "none",
                                  color: config?.textColor || defaultConfig.textColor,
                                  border: "1px solid rgba(0,0,0,0.08)",
                                  fontSize: "14px",
                                  marginBottom: "8px",
                                  wordBreak: "break-all",
                                }}
                              >
                                <DescriptionIcon
                                  style={{ fontSize: 20, color: "#555", flexShrink: 0 }}
                                />
                                <span style={{ fontWeight: 500 }}>{att.title}</span>
                              </a>
                            ))}
                          </div>
                        )}
                    </div>
                    {/* Zobrazenie vývozu odpadu POD bublinou */}
                    {eventList && <div style={{ width: "100%" }}>{eventList}</div>}
                    {/* Zobrazenie nového karuselu pre články */}
                    {msg.articles && Array.isArray(msg.articles) && msg.articles.length > 0 && (
                      <div style={{ width: "100%" }}>
                        <ArticleList articles={msg.articles} />
                      </div>
                    )}
                    {/* Follow-up questions */}
                    {msg.follow_up_questions &&
                      Array.isArray(msg.follow_up_questions) &&
                      msg.follow_up_questions.length > 0 && (
                        <div style={{ marginTop: 16, width: "100%" }}>
                          <div style={followUpTitleStyle}>Návrhy na ďalšie otázky:</div>
                          <div
                            style={{
                              marginTop: 10,
                              display: "flex",
                              flexWrap: "wrap",
                              justifyContent: "flex-start",
                              paddingLeft: 4,
                              width: "100%",
                            }}
                          >
                            {msg.follow_up_questions.map((q, i) => (
                              <button
                                key={i}
                                style={suggestedQuestionStyle}
                                onClick={() => handleSuggestedQuestionClick(q)}
                                onMouseOver={(e) =>
                                  (e.currentTarget.style.background = tinycolor(buttonBase)
                                    .setAlpha(0.1)
                                    .toRgbString())
                                }
                                onMouseOut={(e) => (e.currentTarget.style.background = "none")}
                              >
                                {q}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    {/* Ak sú produkty, zobraz ich pod bublinou na celú šírku */}
                    {msg.products && Array.isArray(msg.products) && msg.products.length > 0 && (
                      <div style={{ width: "100%" }}>
                        <ProductList products={msg.products} title="Najrelevantnejšie produkty" />
                      </div>
                    )}
                  </div>
                );
              }
              // ... pôvodné renderovanie pre user správy ...
              return (
                <div
                  key={idx}
                  style={{ display: "flex", justifyContent: "flex-end", width: "100%" }}
                >
                  <div
                    style={{
                      ...userMsgStyles,
                      ...(expanded && !adminMode ? expandedUserMsgStyles : {}),
                    }}
                  >
                    {msg.content}
                  </div>
                </div>
              );
            })}
            {/* Typing dots keď loading */}
            {loading && (
              <div style={{ display: "flex", justifyContent: "flex-start", width: "100%" }}>
                <div
                  style={{
                    ...assistantMsgStyles,
                    ...(expanded && !adminMode ? expandedAssistantMsgStyles : {}),
                  }}
                >
                  <span style={typingWrapperStyles}>
                    <span style={typingDotStyles}></span>
                    <span style={typingDot2}></span>
                    <span style={typingDot3}></span>
                  </span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
            {error && <div style={{ color: "red", fontSize: 13, marginTop: 8 }}>{error}</div>}
          </div>
          <form
            style={isMobile && !adminMode ? mobileInputBoxStyles : inputBoxStyles}
            onSubmit={handleSend}
          >
            <input
              type="text"
              placeholder="Tvoja správa..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              style={
                isMobile && !adminMode
                  ? mobileInputStyles
                  : { ...inputStyles, ...(expanded && !adminMode ? expandedInputStyles : {}) }
              }
              disabled={loading}
              autoFocus
            />
            {message.trim() && (
              <button
                type="submit"
                style={isMobile && !adminMode ? mobileSendBtnStyles : sendBtnStyles}
                disabled={loading}
                onMouseOver={
                  isMobile && !adminMode
                    ? undefined
                    : (e) => (e.currentTarget.style.background = sendBtnBgHover)
                }
                onMouseOut={
                  isMobile && !adminMode
                    ? undefined
                    : (e) => (e.currentTarget.style.background = sendBtnBg)
                }
              >
                <SendIcon style={{ fontSize: 22, marginLeft: 2 }} />
              </button>
            )}
          </form>
        </div>
      )}
      {/* Bublinu nerenederujem v adminMode a keď je chat otvorený */}
      {!adminMode && !open && (
        <div
          style={
            pillHover
              ? { ...pillButtonStyle(headerBg), ...pillButtonHoverStyles }
              : pillButtonStyle(headerBg)
          }
          onClick={() => setOpen(true)}
          onMouseEnter={() => setPillHover(true)}
          onMouseLeave={() => setPillHover(false)}
          title="Otvoriť chat"
        >
          <div style={pillAvatarContainerStyle}>
            {config.avatar_url ? (
              <img src={config.avatar_url} alt="avatar" style={pillAvatarImageStyle} />
            ) : (
              <span
                style={{
                  fontSize: "22px",
                  fontWeight: "bold",
                  color: tinycolor.mostReadable(headerBg, ["#fff", "#222"]).toHexString(),
                }}
              >
                {config?.name?.[0] || "C"}
              </span>
            )}
          </div>
          <span style={pillTextStyle}>{config.buttonText}</span>
        </div>
      )}
    </div>
  );
}
